import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { whatsappApi } from "@services/api/organizations";
import type { WhatsAppDowntimeGap } from "@services/api/organizations";
import { useWhatsAppStatusStore } from "@store/whatsappStatusStore";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";

/**
 * App-wide WhatsApp outage banner.
 *
 * The failure this exists for: when the WasenderAPI session drops (QR expired,
 * phone logged out), clients keep writing on WhatsApp and their messages NEVER
 * reach the app — no webhook fires, nothing is queued, nothing can be recovered.
 * Before this banner the only signal was an alert email to a single address, so a
 * mid-morning disconnect could go unnoticed for hours.
 *
 * Two things are surfaced:
 *  1. Session down right now → red, permanent (not dismissible), with a QR to fix it.
 *  2. Session recovered but a downtime window was recorded → amber, dismissible,
 *     telling staff to check the phone for messages received during the window.
 */

const Z_BANNER = 9998; // just under the impersonation bar / maintenance overlay

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function fmtDuration(minutes: number | null | undefined): string {
  if (!minutes || minutes < 1) return "moins d'une minute";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
}

function QrReconnectModal({ onClose }: { onClose: () => void }) {
  const fetchStatus = useWhatsAppStatusStore((s) => s.fetch);
  const [qr, setQr] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [manageUrl, setManageUrl] = useState("https://www.wasenderapi.com/whatsapp");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await whatsappApi.getQr();
      setManageUrl(res.manage_url || manageUrl);
      // The backend always returns a rendered data-URL PNG: the raw payload is a
      // device-linking secret and must not be sent to an external QR renderer.
      if (res.available && res.qr?.startsWith("data:")) {
        setQr(res.qr);
        setReason(null);
      } else {
        setQr(null);
        setReason(res.reason || "unavailable");
      }
    } catch {
      setQr(null);
      setReason("error");
    } finally {
      setLoading(false);
    }
  }, [manageUrl]);

  useEffect(() => {
    load();
  }, []);

  // The session flips to "connected" seconds after a successful scan — poll so the
  // modal closes itself instead of making the user guess whether it worked.
  useEffect(() => {
    const t = setInterval(async () => {
      await fetchStatus(true);
      const st = useWhatsAppStatusStore.getState().status;
      if (st?.connected) {
        showNotification({ type: "success", message: "WhatsApp reconnecté" });
        onClose();
      }
    }, 5000);
    return () => clearInterval(t);
  }, [fetchStatus, onClose]);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.55)", zIndex: 10000 }}
      onClick={onClose}
    >
      <div
        className="flex flex-col gap-4 p-6 rounded-2xl"
        style={{ background: "#FFFFFF", width: 460, maxHeight: "88vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-h1 font-semibold" style={{ fontFamily: "Poppins", color: "#111827" }}>
          Reconnecter WhatsApp
        </span>

        {loading ? (
          <span className="text-h3" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
            Génération du QR code…
          </span>
        ) : qr ? (
          <>
            <ol
              className="flex flex-col gap-1 text-h3"
              style={{ fontFamily: "Poppins", color: "#374151", paddingLeft: 18, listStyle: "decimal" }}
            >
              <li>Prenez le téléphone lié au numéro WhatsApp du garage</li>
              <li>WhatsApp → Réglages → Appareils connectés</li>
              <li>« Connecter un appareil » puis scannez ce code</li>
            </ol>
            <div className="flex items-center justify-center p-4 rounded-xl" style={{ background: "#F9FAFB" }}>
              <img src={qr} alt="QR code WhatsApp" style={{ width: 260, height: 260 }} />
            </div>
            <span className="text-h4" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>
              Le QR code expire après ~1 minute. Cliquez sur « Nouveau QR code » s'il ne fonctionne plus.
            </span>
            <div className="flex flex-row gap-2">
              <button
                onClick={load}
                className="text-h3 font-medium px-4 py-2 rounded-lg"
                style={{ fontFamily: "Poppins", background: "#25D366", color: "#FFF" }}
              >
                Nouveau QR code
              </button>
              <button
                onClick={onClose}
                className="text-h3 font-medium px-4 py-2 rounded-lg"
                style={{ fontFamily: "Poppins", color: "#6B7280", border: "1px solid #E5E7EB" }}
              >
                Fermer
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              className="flex flex-col gap-2 p-4 rounded-xl"
              style={{ background: "#FFFBEB", border: "1px solid #FCD34D" }}
            >
              <span className="text-h3 font-semibold" style={{ fontFamily: "Poppins", color: "#92400E" }}>
                {reason === "no_personal_token"
                  ? "QR code non disponible dans l'application"
                  : "Impossible de récupérer le QR code"}
              </span>
              <span className="text-h4" style={{ fontFamily: "Poppins", color: "#92400E" }}>
                {reason === "no_personal_token"
                  ? "La reconnexion depuis l'app nécessite un jeton d'accès WasenderAPI (à configurer une seule fois). En attendant, reconnectez depuis le tableau de bord WasenderAPI."
                  : "Réessayez, ou reconnectez depuis le tableau de bord WasenderAPI."}
              </span>
            </div>
            <div className="flex flex-row gap-2">
              <a
                href={manageUrl}
                target="_blank"
                rel="noreferrer"
                className="text-h3 font-medium px-4 py-2 rounded-lg"
                style={{ fontFamily: "Poppins", background: "#111827", color: "#FFF" }}
              >
                Ouvrir WasenderAPI
              </a>
              <button
                onClick={load}
                className="text-h3 font-medium px-4 py-2 rounded-lg"
                style={{ fontFamily: "Poppins", color: "#374151", border: "1px solid #E5E7EB" }}
              >
                Réessayer
              </button>
              <button
                onClick={onClose}
                className="text-h3 font-medium px-4 py-2 rounded-lg"
                style={{ fontFamily: "Poppins", color: "#6B7280" }}
              >
                Fermer
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function WhatsAppStatusBanner() {
  const orgData = useDataStore((s) => s.orgData);
  const status = useWhatsAppStatusStore((s) => s.status);
  const startPolling = useWhatsAppStatusStore((s) => s.startPolling);
  const stopPolling = useWhatsAppStatusStore((s) => s.stopPolling);
  const fetchStatus = useWhatsAppStatusStore((s) => s.fetch);
  const [showQr, setShowQr] = useState(false);
  const [ackingId, setAckingId] = useState<string | null>(null);

  const waConfigured = !!orgData?.whatsapp?.connected;

  useEffect(() => {
    if (!waConfigured) return;
    startPolling();
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchStatus();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      stopPolling();
    };
  }, [waConfigured, startPolling, stopPolling, fetchStatus]);

  const handleAck = async (gap: WhatsAppDowntimeGap) => {
    setAckingId(gap.id);
    try {
      await whatsappApi.acknowledgeGap(gap.id);
      await fetchStatus(true);
    } catch {
      showNotification({ type: "error", message: "Échec de la confirmation" });
    } finally {
      setAckingId(null);
    }
  };

  if (!waConfigured || !status) return null;

  // Deliberate kill switch: staff still need to know nothing is going out, but this
  // is not an incident — it takes precedence over the outage banner so we don't
  // nag someone to "reconnect" a session they intentionally silenced.
  const sendingDisabled = status.sending_enabled === false;
  const down = !status.connected;
  // Closed, still-unacknowledged windows = "you may have missed messages".
  const pastGaps = (status.gaps || []).filter((g) => !g.open && !g.acknowledged);

  if (sendingDisabled) {
    return (
      <div
        className="fixed top-0 left-0 right-0 w-full"
        style={{ zIndex: Z_BANNER, background: "#FEF3C7", padding: "8px 16px", boxShadow: "0 2px 12px rgba(0,0,0,0.10)" }}
      >
        <div className="flex flex-row items-center justify-center gap-2 flex-wrap">
          <span className="text-h3 font-semibold" style={{ fontFamily: "Poppins", color: "#92400E" }}>
            Envoi WhatsApp désactivé — aucun message ne part vers les clients
            (Réglages → WhatsApp pour réactiver).
          </span>
        </div>
      </div>
    );
  }

  if (!down && pastGaps.length === 0) return null;

  return (
    <>
      {down && (
        <div
          className="fixed top-0 left-0 right-0 w-full"
          style={{
            zIndex: Z_BANNER,
            background: "#B91C1C",
            padding: "10px 16px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
          }}
        >
          <div className="flex flex-row items-center justify-center gap-3 flex-wrap">
            <span
              className="text-h3 font-semibold"
              style={{ fontFamily: "Poppins", color: "#FFFFFF" }}
            >
              WhatsApp déconnecté
              {status.down_since ? ` depuis ${fmtDateTime(status.down_since)}` : ""} —
              {" "}les messages de vos clients n'arrivent plus dans l'application et ne
              pourront pas être récupérés.
            </span>
            <button
              onClick={() => setShowQr(true)}
              className="text-h3 font-semibold px-3 py-1 rounded-lg"
              style={{
                fontFamily: "Poppins",
                background: "#FFFFFF",
                color: "#B91C1C",
                whiteSpace: "nowrap",
              }}
            >
              Reconnecter maintenant
            </button>
          </div>
        </div>
      )}

      {!down &&
        pastGaps.slice(0, 1).map((gap) => (
          <div
            key={gap.id}
            className="fixed top-0 left-0 right-0 w-full"
            style={{
              zIndex: Z_BANNER,
              background: "#FDE68A",
              padding: "10px 16px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
            }}
          >
            <div className="flex flex-row items-center justify-center gap-3 flex-wrap">
              <span
                className="text-h3 font-semibold"
                style={{ fontFamily: "Poppins", color: "#92400E" }}
              >
                WhatsApp a été déconnecté de {fmtTime(gap.started_at)} à{" "}
                {fmtTime(gap.ended_at)} ({fmtDuration(gap.duration_minutes)}) — des
                messages clients envoyés pendant cette période peuvent manquer.
                Vérifiez WhatsApp sur le téléphone du garage.
              </span>
              <button
                onClick={() => handleAck(gap)}
                disabled={ackingId === gap.id}
                className="text-h3 font-semibold px-3 py-1 rounded-lg"
                style={{
                  fontFamily: "Poppins",
                  background: "#92400E",
                  color: "#FFFFFF",
                  whiteSpace: "nowrap",
                  opacity: ackingId === gap.id ? 0.6 : 1,
                }}
              >
                {ackingId === gap.id ? "…" : "J'ai vérifié"}
              </button>
            </div>
          </div>
        ))}

      {showQr && <QrReconnectModal onClose={() => setShowQr(false)} />}
    </>
  );
}

export default WhatsAppStatusBanner;
